import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const labels = {
  CONNECTED: "Liberado",
  WARMUP: "Aquecimento",
  COOLDOWN: "Pausa de descanso",
  BANNED: "Interrompido pela proteção",
};
const localDate = (value, zone) =>
  value ? new Date(value).toLocaleString("pt-BR", { timeZone: zone }) : "—";

export default function WhatsAppProtectionPanel({ companyId }) {
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [acknowledge, setAcknowledge] = useState(false);
  const [resumeQueued, setResumeQueued] = useState(false);
  const [busy, setBusy] = useState(false);
  const refresh = useCallback(async () => {
    if (!companyId) return;
    try {
      const { data } = await api.get("/whatsapp-protection/sessions");
      setSessions(data);
      setError(false);
    } catch (_) {
      setError(true);
    }
  }, [companyId]);
  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const { data } = await api.get("/whatsapp-protection/sessions");
        if (!cancelled) {
          setSessions(data);
          setError(false);
        }
      } catch (_) {
        if (!cancelled) setError(true);
      }
    };
    fetch();
    const timer = setInterval(fetch, 30000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [companyId]);
  const review = async () => {
    setBusy(true);
    try {
      await api.post(`/whatsapp-protection/sessions/${selected.id}/review`, {
        reason,
        resume: true,
        resumeQueued,
        acknowledgeUncertain: acknowledge,
      });
      setSelected(null);
      await refresh();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  if (!sessions.length && !error) return null;
  return (
    <Paper
      variant="outlined"
      style={{
        marginBottom: 16,
        padding: 16,
        overflowX: "auto",
        flexShrink: 0,
      }}
    >
      <Typography variant="h6">Proteção de envios automáticos</Typography>
      <Typography variant="body2" color="textSecondary">
        Campanhas e API compartilham os limites de cada conexão. Janela de
        envio: 08h às 20h no fuso indicado.
      </Typography>
      {error && (
        <Box role="alert" mt={1}>
          <Typography color="error">
            Não foi possível atualizar a proteção.
          </Typography>
          <Button onClick={refresh}>Tentar novamente</Button>
        </Box>
      )}
      <Table
        size="small"
        aria-label="Saúde das conexões para envios automáticos"
      >
        <TableHead>
          <TableRow>
            <TableCell>Conexão</TableCell>
            <TableCell>Proteção</TableCell>
            <TableCell>Envios hoje</TableCell>
            <TableCell>Na fila</TableCell>
            <TableCell>Próxima liberação / fuso</TableCell>
            <TableCell>Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions.map((session) => (
            <TableRow key={session.id}>
              <TableCell>{session.instance_name}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={session.status === "BANNED" ? "secondary" : "default"}
                  label={labels[session.status] || session.status}
                />
                {session.status === "BANNED" && (
                  <Typography role="alert" variant="caption" display="block">
                    Envios suspensos. Revise a conexão e as últimas entregas.
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                {session.messages_sent_today} / {session.daily_limit}
                <Typography variant="caption" display="block">
                  Dia {session.current_warmup_day} do aquecimento
                </Typography>
              </TableCell>
              <TableCell>{session.pending}</TableCell>
              <TableCell>
                {session.status === "BANNED"
                  ? "Aguardando revisão"
                  : localDate(
                      session.cooldown_until || session.next_available_at,
                      session.timezone
                    )}
                <Typography variant="caption" display="block">
                  {session.timezone}
                </Typography>
              </TableCell>
              <TableCell>
                {session.status === "BANNED" && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSelected(session);
                      setReason("");
                      setAcknowledge(false);
                      setResumeQueued(false);
                    }}
                  >
                    Revisar
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog
        open={Boolean(selected)}
        onClose={() => {
          if (!busy) setSelected(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revisar proteção: {selected?.instance_name}</DialogTitle>
        <DialogContent>
          <Typography>
            Verifique a conexão e as últimas mensagens antes de liberar novos
            envios. Esta ação mantém os limites diários e as pausas de descanso.
          </Typography>
          {selected?.uncertain > 0 && (
            <Box mt={2} role="alert">
              <Typography color="error">
                Há {selected.uncertain} mensagem(ns) com entrega incerta.
                Confira o histórico no WhatsApp; elas não serão reenviadas
                automaticamente.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledge}
                    onChange={(event) => setAcknowledge(event.target.checked)}
                  />
                }
                label="Revisei as entregas incertas no histórico"
              />
            </Box>
          )}
          <TextField
            id="whatsapp-protection-review-reason"
            label="Motivo e resultado da revisão"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            fullWidth
            multiline
            margin="normal"
            inputProps={{ maxLength: 1000 }}
            helperText="Descreva a verificação realizada (mínimo de 10 caracteres)."
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={resumeQueued}
                onChange={(event) => setResumeQueued(event.target.checked)}
              />
            }
            label="Liberar também as mensagens que a proteção bloqueou antes de enviar"
          />
          <Typography variant="caption" display="block">
            Campanhas pausadas precisam ser retomadas na tela de campanhas após
            esta revisão.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setSelected(null)}>
            Cancelar
          </Button>
          <Button
            color="primary"
            disabled={
              busy ||
              reason.trim().length < 10 ||
              (selected?.uncertain > 0 && !acknowledge)
            }
            onClick={review}
          >
            {busy ? "Salvando…" : "Registrar revisão e liberar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
